import { useId, useState } from 'react';
import type { RefObject } from 'react';
import { Button } from '../../../components/Button';
import { Radio, RadioGroup } from '../../../components/Radio';
import { SelectPopover } from '../../../components/SelectPopover';
import { AdminInviteError, inviteToOrganization } from '../../../services/adminInvites';
import type {
  InviteRole,
  ResolveCandidate,
  ResolveInviteResponse,
} from '../../../types/adminInvites';
import { sportLabel } from '../../signup/signupFormat';
import { ROLE_COACH, ROLE_ORG_ADMIN, roleKeyFromLabel, roleLabelFromKey } from '../adminRoles';
import { commonMessage, serverMessage } from './inviteForm';
import type { InviteDone } from './inviteTypes';

const ROLE_HINTS: Record<InviteRole, string> = {
  user: 'Sees the pages an organization admin gives them access to.',
  admin: 'Full access to every page, and manages staff access and roles.',
};

const candidateLabel = (c: ResolveCandidate): string => {
  const sport = sportLabel(c.sport);
  return sport ? `${c.name} (${sport})` : c.name;
};

const candidateHint = (c: ResolveCandidate): string => {
  const reason = c.matchReason === 'catalog' ? 'Same school' : 'Same email domain';
  const users = `${c.userCount} ${c.userCount === 1 ? 'user' : 'users'}`;
  return `${reason}, ${users}`;
};

interface InviteMatchStepProps {
  resolved: ResolveInviteResponse;
  headingRef: RefObject<HTMLHeadingElement | null>;
  onCreateNew: () => void;
  onDone: (done: InviteDone) => void;
  onBlocked: (message: string, organizationName: string | null) => void;
}

/**
 * The address matches a program that already exists. Both choices are always offered: invite them
 * into it, or create a new program anyway (plan decision 3).
 */
export const InviteMatchStep = ({
  resolved,
  headingRef,
  onCreateNew,
  onDone,
  onBlocked,
}: InviteMatchStepProps) => {
  const { candidates } = resolved;
  const [selectedId, setSelectedId] = useState(candidates[0]?.organizationId ?? '');
  const [role, setRole] = useState<InviteRole>('user');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const roleId = useId();
  const roleHintId = useId();

  const selected = candidates.find((c) => c.organizationId === selectedId) ?? candidates[0];
  if (!selected) return null;

  const several = candidates.length > 1;
  const heading = several
    ? resolved.school
      ? `${resolved.school.name} already exists in RosterPilot`
      : 'Matching programs already exist in RosterPilot'
    : `${selected.name} already exists in RosterPilot`;

  const openInvitation = resolved.pendingInvitations.find(
    (p) => p.organizationId === selected.organizationId
  );

  const invite = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);

    const base = {
      kind: 'org' as const,
      email: resolved.email,
      organizationId: selected.organizationId,
      organizationName: selected.name,
      role,
    };

    try {
      await inviteToOrganization(selected.organizationId, { email: resolved.email, role });
      onDone({ ...base, sendFailed: false });
    } catch (err: unknown) {
      const failure =
        err instanceof AdminInviteError ? err : new AdminInviteError('failed', '');
      switch (failure.kind) {
        case 'send_failed':
          onDone({ ...base, sendFailed: true });
          return;
        case 'blocked':
          onBlocked(failure.message, failure.organization?.name ?? null);
          return;
        case 'not_found':
          setError('That program no longer exists. Close this and start again.');
          break;
        case 'conflict':
          setError(
            serverMessage(failure.message) ?? 'That address is already active in RosterPilot.'
          );
          break;
        default:
          setError(commonMessage(failure.kind, failure.message));
      }
      setBusy(false);
    }
  };

  return (
    <>
      <h3 className="invite-user-dialog__step-title" ref={headingRef} tabIndex={-1}>
        {heading}
      </h3>
      <p className="invite-user-dialog__text">
        {resolved.email} looks like it belongs to{' '}
        {several ? 'one of these programs' : 'this program'}. Invite them to it, or create a new
        program instead.
      </p>

      <div className="invite-user-dialog__choice">
        {several ? (
          <RadioGroup label="Program" testId="invite-candidates">
            {candidates.map((c) => (
              <Radio
                key={c.organizationId}
                name="invite-candidate"
                value={c.organizationId}
                label={candidateLabel(c)}
                hint={candidateHint(c)}
                checked={c.organizationId === selected.organizationId}
                onChange={() => setSelectedId(c.organizationId)}
                disabled={busy}
                data-testid={`invite-candidate-${c.organizationId}`}
              />
            ))}
          </RadioGroup>
        ) : (
          <p className="invite-user-dialog__hint">
            {candidateLabel(selected)}. {candidateHint(selected)}.
          </p>
        )}

        {selected.isPaused && (
          <p className="invite-user-dialog__notice" data-testid="invite-paused-warning">
            {selected.name} is paused. They can accept, but cannot use it until it is resumed.
          </p>
        )}
        {selected.awaitingPayment && (
          <p className="invite-user-dialog__notice" data-testid="invite-unpaid-warning">
            {selected.name} has not paid yet. They will not get in until it does.
          </p>
        )}
        {openInvitation && (
          <p className="invite-user-dialog__hint">
            An invitation to {selected.name} is already open. Sending again replaces it.
          </p>
        )}

        <div className="invite-user-dialog__field">
          <label className="invite-user-dialog__label" htmlFor={roleId}>
            Role
          </label>
          <SelectPopover
            id={roleId}
            describedById={roleHintId}
            options={[ROLE_COACH, ROLE_ORG_ADMIN]}
            value={roleLabelFromKey(role)}
            onChange={(label) => setRole(roleKeyFromLabel(label) === 'admin' ? 'admin' : 'user')}
            placeholder={ROLE_COACH}
            clearable={false}
            disabled={busy}
            fullWidth
            testId="invite-role"
          />
          <span className="invite-user-dialog__hint" id={roleHintId}>
            {ROLE_HINTS[role]}
          </span>
        </div>

        {error && (
          <p className="invite-user-dialog__error" role="alert">
            {error}
          </p>
        )}

        <div className="invite-user-dialog__actions">
          <Button
            type="button"
            onClick={() => void invite()}
            aria-disabled={busy}
            aria-busy={busy}
            data-testid="invite-to-org"
          >
            {`Invite to ${selected.name}`}
          </Button>
        </div>
      </div>

      <div className="invite-user-dialog__actions invite-user-dialog__actions--split">
        <p className="invite-user-dialog__hint">Not the right program?</p>
        <Button
          type="button"
          variant="secondary"
          onClick={onCreateNew}
          disabled={busy}
          data-testid="invite-create-new"
        >
          Create a new program instead
        </Button>
      </div>
    </>
  );
};
